"use client";

import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useState } from "react";
import { RefreshCw, Home, AlertTriangle } from "lucide-react";

export default function ErrorPage({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations("pageError");
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-background">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -inset-[100%] opacity-30 dark:opacity-20">
          <div className="absolute left-1/2 top-1/3 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-destructive/20 blur-[120px] animate-pulse" />
          <div className="absolute left-1/3 top-2/3 h-[400px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-orange-500/15 blur-[100px] animate-pulse [animation-delay:2s]" />
          <div className="absolute right-1/3 top-1/4 h-[350px] w-[350px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-500/15 blur-[90px] animate-pulse [animation-delay:4s]" />
        </div>

        {/* Grid */}
        <div
          className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto flex max-w-lg flex-col items-center px-4 text-center">
        {/* Error icon */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="mb-6"
        >
          <div className="relative">
            <motion.div
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 0.6, delay: 1, repeat: Infinity, repeatDelay: 5 }}
            >
              <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-destructive/10">
                <AlertTriangle className="h-10 w-10 text-destructive" />
              </div>
            </motion.div>
            {/* Pulsing rings */}
            <motion.div
              className="absolute inset-0 rounded-2xl border border-destructive/20"
              animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </motion.div>

        {/* Error code */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-2"
        >
          <span className="text-sm font-mono font-medium text-destructive/70">
            {t("statusCode")}
          </span>
        </motion.div>

        {/* Decorative line */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mb-6 h-[2px] w-24 origin-center rounded-full bg-gradient-to-r from-transparent via-destructive/40 to-transparent"
        />

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mb-3 text-2xl font-bold sm:text-3xl"
        >
          {t("title")}
        </motion.h1>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mb-8 max-w-sm text-muted-foreground"
        >
          {t("description")}
        </motion.p>

        {/* Action buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="flex flex-wrap items-center justify-center gap-3"
        >
          <Button size="lg" className="gap-2" onClick={reset}>
            <RefreshCw className="h-4 w-4" />
            {t("retry")}
          </Button>
          <Button variant="outline" size="lg" asChild className="gap-2">
            <Link href="/">
              <Home className="h-4 w-4" />
              {t("backToHome")}
            </Link>
          </Button>
        </motion.div>

        {/* Error details toggle */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-10"
        >
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-sm text-muted-foreground/50 underline underline-offset-4 transition hover:text-muted-foreground"
          >
            {showDetails ? t("hideDetails") : t("showDetails")}
          </button>

          {showDetails && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-4 overflow-hidden rounded-lg border border-border bg-muted/50"
            >
              <pre className="max-h-[200px] overflow-auto p-4 text-left font-mono text-xs leading-relaxed text-muted-foreground">
                <code>
                  {error.message || t("unknownError")}
                  {"\n"}
                  {error.stack
                    ?.split("\n")
                    .slice(0, 6)
                    .map((line, i) => (
                      <span key={i}>
                        {"\n"}
                        {line}
                      </span>
                    ))}
                </code>
              </pre>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
